import { useEffect, useState } from "react";
import axios from "axios";
import { MenuItem, Select, TextField, Button, IconButton, Snackbar, Alert } from "@mui/material";
import { Plus, Trash2 } from "react-feather";
import { authData, withAdminAuth } from "../../components/GetAuth";
import CorporateLandingTemplate from "../../../frontend/templates/CorporateLanding";
import CourierDeliveryTemplate from "../../../frontend/templates/CourierDelivery";
import ModernMinimalTemplate from "../../../frontend/templates/ModernMinimalTemplate";
import Loader from "../../components/Loader";
import { CacheSetItem, STORAGE_KEYS, WEBSITE_CACHE_AGE } from "../../components/StorageComp";

const defaultServices = [
    {
        title: "Air Freight",
        description: "Fast and secure air freight solutions worldwide.",
        icon: '/images/air-freight2.png'
    },
    {
        title: "Sea Freight",
        description: "Cost-effective sea freight services for your business.",
        icon: '/images/sea-freight2.png'
    },
    {
        title: "Road Transport",
        description: "Reliable trucking services across borders.",
        icon: '/images/road-ransport.png'
    },
    {
        title: "Warehousing",
        description: "Safe and secure warehousing and distribution.",
        icon: '/images/warehouse.png'
    },
];

// Starting content for each template (website_template_id 1/2/3), used until the company saves its own
const commonDefaults = { serviceTitle: "Our Services", facebookURL: "", instagramURL: "", twitterURL: "", logoURL: "", faviconURL: "", bannerImage: "", aboutImage: "", copyRightText: "All Right Reserved." };

const TEMPLATE_DEFAULTS = {
    1: { ...commonDefaults, heroTitle: "Delivering Global Logistics Solutions", heroSubTitle: "Reliable. Efficient. On Time.", heroDescription: "We provide end-to-end logistics solutions that help your business grow across the world.", aboutTitle: "Your Trusted Logistics Partner", aboutDescription: "With years of experience in the logistics industry, we ensure your goods are delivered safely and on time.", primaryColor: "#001b4d", secondaryColor: "#0d6efd" },
    2: { ...commonDefaults, heroTitle: "We Deliver Your Promise", heroSubTitle: "Fast & Reliable Courier Services", heroDescription: "From local deliveries to global shipping, we make sure your packages reach safely and on time.", aboutTitle: "Your Trusted Logistics Partner", aboutDescription: "With years of experience in the logistics industry, we ensure your goods are delivered safely and on time. Our global network and dedicated team make us your trusted logistics partner.", primaryColor: "#006b2d", secondaryColor: "#222222" },
    3: { ...commonDefaults, heroTitle: "Worldwide Logistics", heroSubTitle: "Moving Business Forward", heroDescription: "Smart logistics solutions for modern businesses. Simple, efficient and reliable.", aboutTitle: "Why Choose Us", aboutDescription: "On-time delivery guarantee\nReal-time shipment tracking\n24/7 customer support\nSecure and flexible solutions", primaryColor: "#6d4cff", secondaryColor: "#111827" },
};

const TEMPLATE_KEY_IDS = { template1: 1, template2: 2, template3: 3 };

// Template defaults with the company's own details; services are copied so edits never change the shared defaults
const templateDefaults = (templateId, companyInfo) => ({
    ...(TEMPLATE_DEFAULTS[templateId] || TEMPLATE_DEFAULTS[1]),
    services: defaultServices.map((s) => ({ ...s })),
    ...companyInfo,
});

const TemplateBuilder = () => {
    const auth = authData();
    const API_TOKEN = auth?.api_token;
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState(null);
    // Until the first save, picking another template reloads that template's starting content
    const [hasSavedConfig, setHasSavedConfig] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    useEffect(() => {
        axios.get(process.env.REACT_APP_API_URL + "v1/company-details", {
            headers: {
                Authorization: `Bearer ${API_TOKEN}`,
            }
        }).then((response) => {
            const company = response.data.data;
            const templateId = company?.website_template_id || TEMPLATE_KEY_IDS[company?.website_template?.template_key] || 1;

            setSelectedTemplate(templateId);

            if (company?.home_config) {
                setHasSavedConfig(true);
                // Older/partial configs may have no services list
                setFormData({ ...company.home_config, services: Array.isArray(company.home_config.services) ? company.home_config.services : [] });
            } else {
                setFormData(templateDefaults(templateId, { companyName: company?.company_name || "", phone: company?.phone || "", email: company?.email || "", address: company?.company_address || "" }));
            }

            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [API_TOKEN]);

    const changeTemplate = (templateId) => {
        setSelectedTemplate(templateId);
        if (!hasSavedConfig) {
            // Keep what is about the company (details, logo, favicon); everything else comes from the template
            setFormData((prev) => templateDefaults(templateId, { companyName: prev?.companyName || "", phone: prev?.phone || "", email: prev?.email || "", address: prev?.address || "", logoURL: prev?.logoURL || "", faviconURL: prev?.faviconURL || "" }));
        }
    }

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const uploadImage = async (file) => {
        try {
            const body = new FormData();

            body.append("image", file);

            const response = await axios.post(process.env.REACT_APP_API_URL + "v1/upload-template-image", body, {
                headers: {
                    Authorization: `Bearer ${API_TOKEN}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            return response.data.url;
        } catch (error) {
            return null;
        }
    };

    const handleImageChange = async (e, field) => {
        const file = e.target.files[0];

        if (!file) return;
        const preview = URL.createObjectURL(file);

        setFormData((prev) => ({ ...prev, [field]: preview }));

        const uploadedURL = await uploadImage(file);

        if (uploadedURL) {
            setFormData((prev) => ({ ...prev, [field]: uploadedURL }));
        }
    }

    const handleServiceChange = (index, field, value) => {
        const updatedServices = [...(formData?.services || [])];
        updatedServices[index][field] = value;

        setFormData({ ...formData, services: updatedServices });
    }

    const addService = () => {
        setFormData({ ...formData, services: [...(formData?.services || []), { title: "", description: "", icon: "🚚" }] });
    }

    const removeService = (index) => {
        const updatedServices = (formData?.services || []).filter((_, i) => i !== index);
        setFormData({ ...formData, services: updatedServices });
    }

    const saveTemplate = () => {
        const payload = { website_template_id: selectedTemplate, home_config: formData };

        setLoading(true);

        axios.post(process.env.REACT_APP_API_URL + "v1/save-home-template", payload, {
            headers: {
                Authorization: `Bearer ${API_TOKEN}`,
            }
        }).then((response) => {
            setLoading(false);
            if (response.data.status === "SUCCESS") setHasSavedConfig(true);
            setSnackbarOpen(true);
            setSnackbarMessage("Template Saved Successfully");
            setSnackbarType("success");

            CacheSetItem(STORAGE_KEYS.WEBSITE, response.data.data, WEBSITE_CACHE_AGE);
        }).catch(() => {
            setLoading(false);
            setSnackbarOpen(true);
            setSnackbarMessage("Something went wrong");
            setSnackbarType("error");
        });
    }

    return (<>
        <Loader open={loading} setOpen={setLoading.bind(this)} />

        <div style={{ display: "grid", gridTemplateColumns: "620px 1fr", minHeight: "100vh", background: "#f5f7fb" }}>
            <div style={{ background: "#fff", padding: "30px", overflowY: "auto", borderRight: "1px solid #e5e7eb", height: "100vh" }}>
                <h2 style={{ marginBottom: "30px", fontSize: "32px" }}>Template Builder</h2>

                <div style={field}>
                    <label style={label}>Select Template</label>
                    <Select value={selectedTemplate || ""} onChange={(e) => changeTemplate(e.target.value)} style={input} fullWidth>
                        <MenuItem value={1}>Corporate Logistics</MenuItem>
                        <MenuItem value={2}>Courier Delivery</MenuItem>
                        <MenuItem value={3}>Modern Minimal</MenuItem>
                    </Select>
                </div>

                <div style={field}>
                    <label style={label}>Company Name</label>
                    <TextField name="companyName" value={formData?.companyName} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Phone</label>
                    <TextField name="phone" value={formData?.phone} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Email</label>
                    <TextField name="email" value={formData?.email} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Address</label>
                    <TextField multiline rows={2} name="address" value={formData?.address} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Hero Title</label>
                    <TextField name="heroTitle" value={formData?.heroTitle} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Hero Subtitle</label>
                    <TextField name="heroSubTitle" value={formData?.heroSubTitle} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Hero Description</label>
                    <TextField multiline rows={3} name="heroDescription" value={formData?.heroDescription} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Service Title</label>
                    <TextField name="serviceTitle" value={formData?.serviceTitle} onChange={handleChange} fullWidth />
                </div>

                <div style={{ marginTop: "40px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3>Services</h3>
                    <Button variant="contained" className="TableAddBU" startIcon={<Plus size={16} />} onClick={addService}>Add</Button>
                </div>

                {(formData?.services || []).map((service, index) => (<div key={index} style={{ border: "1px solid #e5e7eb", padding: "20px", marginBottom: "20px", background: "#fafafa" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
                        <h4>Service{" "}{index + 1}</h4>
                        <IconButton onClick={() => removeService(index)}>
                            <Trash2 size={18} color="red" />
                        </IconButton>
                    </div>

                    <div style={field}>
                        <label style={label}>Icon</label>
                        <input type="file" accept="image/*"
                            onChange={async (e) => {
                                const file = e.target.files[0];

                                if (file) {
                                    const imageUrl = URL.createObjectURL(file);

                                    handleServiceChange(index, "icon", imageUrl);

                                    const uploadedURL = await uploadImage(file);

                                    if (uploadedURL) {
                                        handleServiceChange(index, "icon", uploadedURL);
                                    }
                                }
                            }}
                        />
                    </div>

                    <div style={field}>
                        <label style={label}>Title</label>
                        <TextField value={service.title} onChange={(e) => handleServiceChange(index, "title", e.target.value)} fullWidth />
                    </div>

                    <div style={field}>
                        <label style={label}>Description</label>
                        <TextField multiline rows={3} value={service.description} onChange={(e) => handleServiceChange(index, "description", e.target.value)} fullWidth />
                    </div>
                </div>))}

                <div style={field}>
                    <label style={label}>About Title</label>
                    <TextField name="aboutTitle" value={formData?.aboutTitle} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>About Description</label>
                    <TextField multiline rows={4} name="aboutDescription" value={formData?.aboutDescription} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Copyright Text</label>
                    <TextField name="copyRightText" value={formData?.copyRightText} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Facebook URL</label>
                    <TextField name="facebookURL" value={formData?.facebookURL} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Instagram URL</label>
                    <TextField name="instagramURL" value={formData?.instagramURL} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Twitter URL</label>
                    <TextField name="twitterURL" value={formData?.twitterURL} onChange={handleChange} fullWidth />
                </div>

                <div style={field}>
                    <label style={label}>Primary Color</label>
                    <input type="color" name="primaryColor" value={formData?.primaryColor} onChange={handleChange} style={colorInput} />
                </div>

                <div style={field}>
                    <label style={label}>Secondary Color</label>
                    <input type="color" name="secondaryColor" value={formData?.secondaryColor} onChange={handleChange} style={colorInput} />
                </div>

                <div style={field}>
                    <label style={label}>Logo</label>
                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "logoURL")} />
                </div>

                <div style={field}>
                    <label style={label}>Favicon</label>
                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "faviconURL")} />
                </div>

                <div style={field}>
                    <label style={label}>Banner Image</label>
                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "bannerImage")} />
                </div>

                <div style={field}>
                    <label style={label}>About Image</label>
                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "aboutImage")} />
                </div>

                <Button variant="contained" onClick={saveTemplate} className="TableAddBU" style={{ marginTop: "30px", width: "100%", height: "55px", background: "#ff0033", color: "#fff" }}>Save Template</Button>
            </div>

            <div style={{ overflowY: "auto", height: "100vh", background: "#e5e7eb", padding: "30px" }}>
                <div style={{ background: "#fff", minHeight: "100%" }}>
                    {selectedTemplate === 1 && (<CorporateLandingTemplate data={formData} isStickyHeader={false} />)}

                    {selectedTemplate === 2 && (<CourierDeliveryTemplate data={formData} isStickyHeader={false} />)}

                    {selectedTemplate === 3 && (<ModernMinimalTemplate data={formData} isStickyHeader={false} />)}
                </div>
            </div>
        </div>

        <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={(e) => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <Alert
                onClose={(e) => setSnackbarOpen(false)}
                severity={snackbarType}
                variant="filled"
                sx={{ width: "100%" }}
            >
                {snackbarMessage}
            </Alert>
        </Snackbar>
    </>);
};

const field = {
    marginBottom: "22px",
};

const label = {
    display: "block",
    marginBottom: "10px",
    fontWeight: "600",
    fontSize: "15px",
};

const input = {
    width: "100%",
    height: "52px",
};

const colorInput = {
    width: "100%",
    height: "55px",
    border: "none",
    background: "none",
};

export default withAdminAuth(TemplateBuilder);